import { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical values sent to backend (do NOT translate these).
const VERTICAL_KEYS = [
    "IoT",
    "Health",
    "AI",
    "Climate",
    "Industrial & Infrastructure",
    "Deep Tech",
    "Other",
];
const STAGE_KEYS = ["First customers", "Established traction", "Scale"];
const REVENUE_KEYS = ["Pre-revenue", "<50k€", "50k–200k€", "200k–1M€", "1M€+"];
const NEEDS_KEYS = [
    "Capital",
    "GTM / Market access",
    "Europe entry",
    "Board / Governance",
    "Combination",
];

const INITIAL_FORM = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    vertical: "",
    vertical_other: "",
    stage: "",
    description: "",
    needs: [],
    challenges: "",
    revenue_range: "",
    target_geography: "",
    // honeypot — humans never fill this
    website: "",
};

function Field({ label, htmlFor, children, hint }) {
    return (
        <div>
            <label
                htmlFor={htmlFor}
                className="block text-[10px] uppercase tracking-[0.14em] text-[#C9A96E] mb-1 font-medium"
            >
                {label}
            </label>
            {children}
            {hint && (
                <p className="mt-1 text-[12px] text-[rgba(232,230,223,0.4)]">
                    {hint}
                </p>
            )}
        </div>
    );
}

function SectionHeader({ n, title }) {
    return (
        <div className="flex items-baseline gap-4 mb-6 mt-2">
            <span className="font-display text-[13px] text-[#C9A96E] tracking-[0.14em] font-medium">
                {n}
            </span>
            <h3 className="text-[12px] uppercase tracking-[0.14em] text-[rgba(232,230,223,0.75)] font-medium">
                {title}
            </h3>
        </div>
    );
}

export default function ContactForm() {
    const { t } = useT();
    const f = t.contact.fields;

    const [form, setForm] = useState(INITIAL_FORM);
    const [status, setStatus] = useState({ type: null, message: "" });
    const [submitting, setSubmitting] = useState(false);

    const labelByKey = useMemo(
        () => ({
            first_name: f.firstName,
            last_name: f.lastName,
            email: f.email,
            phone: f.phone,
            company: f.company,
            vertical: f.vertical,
            stage: f.stage,
            description: f.description,
            challenges: f.challenges,
            revenue_range: f.revenue,
            target_geography: f.targetGeo,
        }),
        [f],
    );

    const update = (k) => (e) =>
        setForm((prev) => ({ ...prev, [k]: e.target.value }));

    const toggleNeed = (n) =>
        setForm((prev) => ({
            ...prev,
            needs: prev.needs.includes(n)
                ? prev.needs.filter((x) => x !== n)
                : [...prev.needs, n],
        }));

    const validate = () => {
        const required = [
            "first_name",
            "last_name",
            "email",
            "phone",
            "company",
            "vertical",
            "stage",
            "description",
            "challenges",
            "revenue_range",
            "target_geography",
        ];
        for (const k of required) {
            if (!String(form[k] || "").trim()) {
                return `${t.contact.errorMissing} ${labelByKey[k] || k}`;
            }
        }
        if (form.needs.length === 0) {
            return t.contact.errorAtLeastOne;
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            return t.contact.errorEmail;
        }
        if (!form.consent) {
            return t.contact.errorConsent;
        }
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            setStatus({ type: "error", message: err });
            return;
        }
        setSubmitting(true);
        setStatus({ type: null, message: "" });
        try {
            const payload = { ...form };
            if (payload.vertical !== "Other") payload.vertical_other = "";
            delete payload.consent;
            await axios.post(`${API}/ventures`, payload);
            setStatus({ type: "success", message: t.contact.success });
            setForm(INITIAL_FORM);
        } catch (e2) {
            const httpStatus = e2?.response?.status;
            const message =
                httpStatus === 429
                    ? t.contact.errorRateLimit
                    : e2?.response?.data?.detail || t.contact.errorGeneric;
            setStatus({ type: "error", message });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 border-t border-[rgba(201,169,110,0.10)] overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{
                    backgroundImage:
                        "url('https://static.prod-images.emergentagent.com/jobs/a62b47d9-28ab-47fe-91c2-f4f8edce9f70/images/02e47bd8137ee3f63a965402be277844c2d70c4b8ca0fa5c0d63195a13803717.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />

            <div className="relative max-w-[1280px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-12 lg:gap-20">
                <div>
                    <div className="lg:sticky lg:top-32">
                        <p className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-6">
                            {t.contact.label}
                        </p>
                        <h2 className="font-display text-[28px] md:text-[40px] font-medium leading-[1.1] tracking-[-0.02em] mb-6">
                            <span className="text-[#C9A96E]">
                                {t.contact.headlineAccent}
                            </span>{" "}
                            {t.contact.headlineRest}
                        </h2>
                        <p className="text-[15px] leading-[1.7] text-[rgba(232,230,223,0.65)] max-w-[400px]">
                            {t.contact.subtitle}
                        </p>
                        <p className="mt-6 text-[11px] uppercase tracking-[0.14em] text-[rgba(232,230,223,0.4)]">
                            {t.contact.noDeck.map((line, i) => (
                                <span key={i}>
                                    {line}
                                    {i < t.contact.noDeck.length - 1 && <br />}
                                </span>
                            ))}
                        </p>
                    </div>
                </div>

                <motion.form
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    onSubmit={onSubmit}
                    data-testid="venture-form"
                    noValidate
                    className="space-y-12"
                >
                    {/* Honeypot — visually hidden, off-screen, ignored by humans */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            left: "-10000px",
                            top: "auto",
                            width: "1px",
                            height: "1px",
                            overflow: "hidden",
                        }}
                    >
                        <label htmlFor="website">Website</label>
                        <input
                            id="website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={update("website")}
                        />
                    </div>

                    {/* Section 1 — Identification */}
                    <div>
                        <SectionHeader n="01" title={t.contact.sections.s1} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <Field label={f.firstName} htmlFor="first_name">
                                <input
                                    id="first_name"
                                    name="first_name"
                                    type="text"
                                    required
                                    className="alpe-input"
                                    value={form.first_name}
                                    onChange={update("first_name")}
                                    data-testid="input-first-name"
                                />
                            </Field>
                            <Field label={f.lastName} htmlFor="last_name">
                                <input
                                    id="last_name"
                                    name="last_name"
                                    type="text"
                                    required
                                    className="alpe-input"
                                    value={form.last_name}
                                    onChange={update("last_name")}
                                    data-testid="input-last-name"
                                />
                            </Field>
                            <Field label={f.email} htmlFor="email">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    className="alpe-input"
                                    value={form.email}
                                    onChange={update("email")}
                                    data-testid="input-email"
                                />
                            </Field>
                            <Field label={f.phone} htmlFor="phone">
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    required
                                    className="alpe-input"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    data-testid="input-phone"
                                />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label={f.company} htmlFor="company">
                                    <input
                                        id="company"
                                        name="company"
                                        type="text"
                                        required
                                        className="alpe-input"
                                        value={form.company}
                                        onChange={update("company")}
                                        data-testid="input-company"
                                    />
                                </Field>
                            </div>
                        </div>
                    </div>

                    {/* Section 2 — Your venture */}
                    <div>
                        <SectionHeader n="02" title={t.contact.sections.s2} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <Field label={f.vertical} htmlFor="vertical">
                                <select
                                    id="vertical"
                                    name="vertical"
                                    required
                                    className="alpe-select"
                                    value={form.vertical}
                                    onChange={update("vertical")}
                                    data-testid="select-vertical"
                                >
                                    <option value="">
                                        {f.selectPlaceholder}
                                    </option>
                                    {VERTICAL_KEYS.map((v) => (
                                        <option key={v} value={v}>
                                            {v === "Other"
                                                ? f.otherOption
                                                : t.verticalOptions[v] || v}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label={f.stage} htmlFor="stage">
                                <select
                                    id="stage"
                                    name="stage"
                                    required
                                    className="alpe-select"
                                    value={form.stage}
                                    onChange={update("stage")}
                                    data-testid="select-stage"
                                >
                                    <option value="">
                                        {f.selectPlaceholder}
                                    </option>
                                    {STAGE_KEYS.map((s) => (
                                        <option key={s} value={s}>
                                            {t.stageOptions[s] || s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <div className="md:col-span-2">
                                <Field
                                    label={f.description}
                                    htmlFor="description"
                                >
                                    <input
                                        id="description"
                                        name="description"
                                        type="text"
                                        required
                                        className="alpe-input"
                                        placeholder={f.descriptionPlaceholder}
                                        value={form.description}
                                        onChange={update("description")}
                                        data-testid="input-description"
                                    />
                                </Field>
                            </div>
                            {form.vertical === "Other" && (
                                <div className="md:col-span-2">
                                    <Field
                                        label={f.verticalOther}
                                        htmlFor="vertical_other"
                                        hint={f.verticalOtherHint}
                                    >
                                        <input
                                            id="vertical_other"
                                            name="vertical_other"
                                            type="text"
                                            className="alpe-input"
                                            value={form.vertical_other}
                                            onChange={update("vertical_other")}
                                            data-testid="input-vertical-other"
                                        />
                                    </Field>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Section 3 — What you need */}
                    <div>
                        <SectionHeader n="03" title={t.contact.sections.s3} />
                        <p className="text-[12px] text-[rgba(232,230,223,0.45)] mb-5">
                            {f.needsHint}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                            {NEEDS_KEYS.map((n) => (
                                <label
                                    key={n}
                                    className="flex items-center gap-4 cursor-pointer group py-2 border-b border-[rgba(201,169,110,0.10)] hover:border-[rgba(201,169,110,0.30)] transition-colors"
                                    data-testid={`checkbox-need-${n
                                        .toLowerCase()
                                        .replace(/\W+/g, "-")}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="alpe-check"
                                        checked={form.needs.includes(n)}
                                        onChange={() => toggleNeed(n)}
                                    />
                                    <span className="text-[15px] text-[#E8E6DF] group-hover:text-[#C9A96E] transition-colors">
                                        {t.needsOptions[n] || n}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Section 4 — Challenges */}
                    <div>
                        <SectionHeader n="04" title={t.contact.sections.s4} />
                        <Field label={f.challenges} htmlFor="challenges">
                            <textarea
                                id="challenges"
                                name="challenges"
                                required
                                rows={5}
                                className="alpe-textarea"
                                placeholder={f.challengesPlaceholder}
                                value={form.challenges}
                                onChange={update("challenges")}
                                data-testid="input-challenges"
                            />
                        </Field>
                    </div>

                    {/* Section 5 — Qualification */}
                    <div>
                        <SectionHeader n="05" title={t.contact.sections.s5} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <Field label={f.revenue} htmlFor="revenue_range">
                                <select
                                    id="revenue_range"
                                    name="revenue_range"
                                    required
                                    className="alpe-select"
                                    value={form.revenue_range}
                                    onChange={update("revenue_range")}
                                    data-testid="select-revenue"
                                >
                                    <option value="">
                                        {f.selectPlaceholder}
                                    </option>
                                    {REVENUE_KEYS.map((r) => (
                                        <option key={r} value={r}>
                                            {t.revenueOptions[r] || r}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field
                                label={f.targetGeo}
                                htmlFor="target_geography"
                            >
                                <input
                                    id="target_geography"
                                    name="target_geography"
                                    type="text"
                                    required
                                    className="alpe-input"
                                    placeholder={f.targetGeoPlaceholder}
                                    value={form.target_geography}
                                    onChange={update("target_geography")}
                                    data-testid="input-target-geography"
                                />
                            </Field>
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="pt-2">
                        {/* GDPR consent */}
                        <label
                            className="flex items-start gap-3 mb-6 cursor-pointer group"
                            data-testid="consent-label"
                        >
                            <input
                                type="checkbox"
                                className="alpe-check mt-[3px]"
                                checked={form.consent}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        consent: e.target.checked,
                                    }))
                                }
                                data-testid="consent-checkbox"
                            />
                            <span className="text-[13px] leading-[1.6] text-[rgba(232,230,223,0.65)] group-hover:text-[rgba(232,230,223,0.85)] transition-colors">
                                {t.contact.consentBefore}{" "}
                                <a
                                    href="/privacy"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[#C9A96E] hover:text-[#d4b986] underline-offset-2 hover:underline"
                                >
                                    {t.contact.consentLink}
                                </a>
                                {t.contact.consentAfter}
                            </span>
                        </label>

                        {status.type === "success" && (
                            <div
                                role="status"
                                data-testid="form-success"
                                className="mb-6 p-5 border border-[rgba(201,169,110,0.35)] bg-[rgba(201,169,110,0.05)] rounded-[4px] text-[14px] leading-[1.6] text-[#E8E6DF]"
                            >
                                <span className="text-[#C9A96E] mr-2">✓</span>
                                {status.message}
                            </div>
                        )}
                        {status.type === "error" && (
                            <div
                                role="alert"
                                data-testid="form-error"
                                className="mb-6 p-5 border border-[rgba(220,90,90,0.4)] bg-[rgba(220,90,90,0.06)] rounded-[4px] text-[14px] leading-[1.6] text-[#E8E6DF]"
                            >
                                {status.message}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="form-submit"
                            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-[28px] py-[14px] bg-[#C9A96E] text-[#0D0D1A] rounded-[4px] text-[14px] font-medium hover:bg-[#d4b986] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                        >
                            {submitting ? t.contact.submitting : t.contact.submit}
                            {!submitting && <span aria-hidden="true">→</span>}
                        </button>
                        <p className="mt-4 text-[12px] text-[rgba(232,230,223,0.45)]">
                            {t.contact.submitNote}
                        </p>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}
